import { LoginPage } from "@presentation/pages";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Sidebar } from "@presentation/components/layout/sidebar/Sidebar"; // import {Sidebar} from '@presentation/components'; for some reason importing is nto workign this way with this component only...
import { useUserManagementStore } from "@presentation/stores/UserManagmentStore";
import { NotificationProvider } from "@presentation/components/notifications";
import React, { memo, useEffect, useState } from "react";
import "@utils/stringLocalization/i18n";
import OidcCallback from "./components/auth/OidcCallback";
import { useInjection } from "./components/dependencyInjection/useInjection";
import { AuthService } from "@infrastructure/services";
import { ListSoftwareVersionsService } from "@application/useCases/softwareVersions/ListSoftwareVersionsService";
import { FullscreenLoader } from "@presentation/components/notifications";
import { logOutInProgress, signInProgress, spectronautVersionsSeenByUserStoragePrefix } from "@presentation/components/helpers/constants";
import { InactivityAutoSignOut } from "./components/auth";
import { UploadProgressIndicator } from "@presentation/components";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { appRoutes } from "@presentation/AppRoutes";

const MemoizedRoutes = memo(() => (
  <Routes>
    {appRoutes.map((route, index) => (
      <Route key={index} path={route.route} element={route.element} />
    ))}
  </Routes>
));

const normalizeVersions = (versions: string[]): string[] => [...new Set(versions.map((version) => version.trim()).filter(Boolean))];

const formatNewVersionsMessage = (versions: string[]): string => {
  if (versions.length === 1) {
    return `A new Spectronaut version is available: ${versions[0]}.`;
  }

  if (versions.length <= 3) {
    return `New Spectronaut versions are available: ${versions.join(", ")}.`;
  }

  const preview = versions.slice(0, 3);
  return `New Spectronaut versions are available: ${preview.join(", ")}, and ${versions.length - preview.length} more.`;
};

const App: React.FC = () => {
  const [checkedSession, setCheckedSession] = useState(false);
  const [newSpectronautVersions, setNewSpectronautVersions] = useState<string[]>([]);
  const [showNewVersionDialog, setShowNewVersionDialog] = useState(false);
  const username = useUserManagementStore((state) => state.userSession?.username);
  const authService = useInjection(AuthService);
  const versionsService = useInjection(ListSoftwareVersionsService);

  useEffect(() => {
    if (username) {
      return;
    }

    setShowNewVersionDialog(false);
    setNewSpectronautVersions([]);
  }, [username]);
  useEffect(() => {
    const wasLoggedOut = sessionStorage.getItem(logOutInProgress) === "true";
    const wasSignInStarted = sessionStorage.getItem(signInProgress) === "true";
    if (wasLoggedOut || wasSignInStarted) {
      console.info("Skipping session restore");
      sessionStorage.removeItem(logOutInProgress); // Clear the flag so it doesn't persist
      setCheckedSession(true);
      return; // Skip session restore
    }
    console.info("Attempting to restore session");
    const tryRestoreSession = async () => {
      const session = useUserManagementStore.getState().userSession;
      const token = session?.accessToken ?? null;
      if (session.username) console.info("Token is present");

      if (token && !authService.isTokenExpired(token)) {
        console.info("Session restored from memory");
        setCheckedSession(true);
        return;
      }

      try {
        console.info("Attempting to refresh access token...");
        const refreshedToken = await authService.refreshAccessToken();

        if (refreshedToken) {
          console.info("Access token refreshed successfully");
          setCheckedSession(true);
          return;
        }

        throw new Error("No token returned from refresh");
      } catch (refreshError) {
        console.warn("Token refresh failed, falling back to sign-in:", refreshError);

        try {
          await authService.signIn();
          console.info("Session restored via sign-in");
        } catch (signInError) {
          console.warn("Sign-in failed:", signInError);
          useUserManagementStore.getState().clear();
        } finally {
          setCheckedSession(true);
        }
      }
    };

    tryRestoreSession();
  }, [authService]);

  useEffect(() => {
    if (!checkedSession || !username) {
      return;
    }

    let isCancelled = false;

    const checkForNewSpectronautVersions = async () => {
      try {
        const candidateVersions = await versionsService.getCandidateVersionsListAsync();
        if (isCancelled || candidateVersions.length === 0) {
          return;
        }

        const storageKey = `${spectronautVersionsSeenByUserStoragePrefix}:${username}`;
        const rawSeenVersions = localStorage.getItem(storageKey);
        const normalizedCandidates = normalizeVersions(candidateVersions);

        if (normalizedCandidates.length === 0) {
          return;
        }

        // First login (or fresh/incognito session): initialize baseline without showing a popup.
        if (!rawSeenVersions) {
          localStorage.setItem(storageKey, JSON.stringify(normalizedCandidates));
          return;
        }

        let seenVersions = new Set<string>();
        if (rawSeenVersions) {
          try {
            const parsedSeenVersions = JSON.parse(rawSeenVersions);
            if (Array.isArray(parsedSeenVersions)) {
              seenVersions = new Set(normalizeVersions(parsedSeenVersions.map(String)));
            } else {
              localStorage.setItem(storageKey, JSON.stringify(normalizedCandidates));
              return;
            }
          } catch {
            localStorage.setItem(storageKey, JSON.stringify(normalizedCandidates));
            return;
          }
        }
        const discoveredVersions = normalizedCandidates.filter((version) => !seenVersions.has(version));

        if (discoveredVersions.length > 0) {
          setNewSpectronautVersions(discoveredVersions);
          setShowNewVersionDialog(true);
        }

        localStorage.setItem(storageKey, JSON.stringify(normalizedCandidates));
      } catch (error) {
        console.warn("Unable to check Spectronaut image tags", error);
      }
    };

    void checkForNewSpectronautVersions();

    return () => {
      isCancelled = true;
    };
  }, [checkedSession, username, versionsService]);

  if (!checkedSession) {
    return <FullscreenLoader />;
  }

  return (
    <NotificationProvider>
      {/* Tooltip provider: must be placed in App.tsx to enable tooltips across the app.
      It automatically shows a tooltip for any child element with a 'title' attribute.
      'parentTitle={true}' ensures the tooltip also works when the 'title' is on a parent element,
      which is especially useful for icon-only buttons. */}
      <Tooltip position="top" parentTitle={true} />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {username ? (
          <>
            {checkedSession && <InactivityAutoSignOut />}
            <Sidebar routes={appRoutes}>
              <MemoizedRoutes />
              <UploadProgressIndicator />
            </Sidebar>
          </>
        ) : (
          <Routes>
            <Route path="*" element={<LoginPage />} />
            <Route path="/auth/callback" element={<OidcCallback />} />
          </Routes>
        )}
      </BrowserRouter>
      {showNewVersionDialog && username && (
        <Dialog title="New Spectronaut Version Available" width={520} onClose={() => setShowNewVersionDialog(false)}>
          <p style={{ marginTop: 0 }}>{formatNewVersionsMessage(newSpectronautVersions)}</p>
          <p>Go to the Software Versions tab to review and manage available versions.</p>
          <DialogActionsBar>
            <Button onClick={() => setShowNewVersionDialog(false)}>Dismiss</Button>
            <Button
              themeColor="primary"
              onClick={() => {
                setShowNewVersionDialog(false);
                location.assign("/software-versions");
              }}
            >
              Open Software Versions
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </NotificationProvider>
  );
};

export default App;
